// Authentication utilities for JWT token management
import axios from 'axios';
import { API_BASE_URL } from '../config';

const TOKEN_KEY = 'vermillion_token';
const USER_KEY = 'vermillion_user';

// Get stored token
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

// Get stored user
export const getUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
};

// Store token and user
export const setAuth = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

// Clear auth data
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getToken();
};

// Setup axios interceptor to include JWT token in requests
export const setupAxiosInterceptors = () => {
  // Request interceptor to add token
  axios.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle 401 errors
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const msg = String(error.response?.data?.msg || error.response?.data?.error || '').toLowerCase();
      const jwtRejected = status === 401 || (
        status === 422 && (
          msg.includes('token') ||
          msg.includes('authorization') ||
          msg.includes('segment') ||
          msg.includes('signature')
        )
      );
      if (jwtRejected) {
        // Token expired or invalid
        clearAuth();
        const path = window.location.pathname || '';
        if (path !== '/login') {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );
};

// Login function
export const login = async (username, password, tenantName) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/login`, {
      username,
      password,
      tenant_name: tenantName
    });
    
    if (response.data.access_token) {
      // Store tenant info with user
      const userData = {
        ...response.data.user,
        tenant: response.data.tenant
      };
      setAuth(response.data.access_token, userData);
      return { success: true, user: userData };
    }
    return { success: false, error: 'Invalid response from server' };
  } catch (error) {
    // Provide more specific error messages
    if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
      return {
        success: false,
        error: `Cannot connect to server. Please ensure the backend is running at ${API_BASE_URL}`
      };
    }
    if (error.response) {
      // Server responded with error status
      return {
        success: false,
        error: error.response.data?.error || `Server error: ${error.response.status}`
      };
    }
    return {
      success: false,
      error: error.message || 'Login failed. Please try again.'
    };
  }
};

// Register function
export const register = async (username, password, email, tenantName, companyName) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/register`, {
      username,
      password,
      email,
      tenant_name: tenantName,
      company_name: companyName
    });
    
    if (response.data.access_token) {
      setAuth(response.data.access_token, response.data.user);
      return { success: true, user: response.data.user };
    }
    return { success: false, error: 'Invalid response from server' };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Registration failed'
    };
  }
};

// Logout function
export const logout = () => {
  clearAuth();
  window.location.href = '/login';
};

